import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 경영보고 — 경영진이 매달 받아 가는 한 장.
// 여기서 새로 계산하는 숫자는 없다. 손익은 위하고에서 받아 담아 둔 것, 자금은 은행·증권
// 원장에서 그대로 모은 것이다. 인쇄를 누르면 그대로 PDF가 된다.

const 백만 = (v) => (v == null ? '—' : Math.round(v / 1e6).toLocaleString())
const 억 = (v) => (v == null ? '—' : (v / 1e8).toFixed(1))
const 률 = (a, b) => (a == null || !b ? null : (a / b) * 100)
const pct = (v) => (v == null ? '—' : v.toFixed(1) + '%')
const 월목록 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

// toISOString은 UTC로 바꾸므로 한국 시간 새벽에 달이 하루 밀린다. 지역 시간으로 짓는다.
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

export default function ExecReport({ currentUserId, autoOpenNoteId, onAutoOpened }) {
  const 오늘 = new Date()
  const 이번달 = ym(오늘)
  // 기본값은 이번 달이다. 지난달로 열면 더 정확할 것 같지만 실제로는 반대다 - 분류는
  // 최근 달부터 붙여 나가고 있어서, 지난달을 열면 신뢰도 칸이 한 자릿수로 떨어진다.
  // 이번 달이 아직 안 끝나 수지가 반쪽인 것은 날짜를 보면 알 수 있지만, 분류율이 왜
  // 낮은지는 화면만 봐서는 알 수 없다.
  const [month, setMonth] = useState(이번달)
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    setErr(''); setD(null)
    portal.get(`/api/dashboard/exec-report?month=${month}`)
      .then(setD)
      .catch((e) => setErr(e.status === 403
        ? '경영보고 화면 권한이 없습니다. 관리자에게 권한을 요청하세요.' : e.message))
  }, [month])

  if (err) return <><h2>경영보고</h2><div className="error-box">{err}</div></>
  if (!d) return <><h2>경영보고</h2><div className="muted">불러오는 중…</div></>

  const 사업부 = d['손익']['사업부'] || []
  // 회사 전체 줄의 이름은 서버가 알려 준다 - 위하고 파일이 바뀌며 「에르모어」가 사업부가
  // 되고 전체가 따로 왔다. 이름을 여기 박아 두면 그날 회사 매출이 사업부 하나치가 된다.
  const 합계이름 = d['손익']['합계사업부'] || '전체'
  const 합계 = d['손익']['요약'][합계이름] || {}
  const 끝월 = d['손익']['마지막월'] || 0
  const v = (s, k) => (s && s[k] ? s[k]['합계'] : null)
  const mv = (s, k, m) => (s && s[k] ? s[k][`m${m}`] : 0)

  const 매출 = v(합계, '매출액')
  const 영업 = v(합계, '영업이익')
  const 순익 = v(합계, '당기순이익')
  const 잔액 = d['잔액']
  const 수지 = d['수지']
  const 쓰는달 = 월목록.filter((m) => 끝월 && m <= 끝월)
  const 현금달 = 월목록.filter((m) => (수지['수입'][m] || 0) || (수지['지출'][m] || 0))
  const 이번 = Number(month.slice(5, 7))
  const 당월수입 = 수지['수입'][이번] || 0
  const 당월지출 = 수지['지출'][이번] || 0
  const 당월대체 = (d['수지']['대체'] || {})[이번] || 0
  // 지출은 크기만 담겨 있다. 표기할 때 부호를 붙인다 - -0이 찍히지 않게 0은 그대로 둔다.
  const 음 = (v) => (v ? 백만(-v) : 백만(0))
  const 기준월 = Number(잔액['기준일'].slice(5, 7))
  const 기준연 = Number(잔액['기준일'].slice(0, 4))
  // 아직 안 끝난 달은 월말잔액이 없다. 그 달만 기준일 현재 잔액으로 채운다.
  const 월잔액 = (m) => {
    const v = 잔액['월말'][m]
    if (v != null) return v
    if (m === 기준월 && 기준연 === d['year']) return 잔액['은행']
    return null
  }
  const 계획수입 = d['계획']['수입'][이번] || 0
  const 계획지출 = d['계획']['지출'][이번] || 0

  const 원가 = d['원가'] || {}
  const 신뢰 = d['신뢰도']
  const 분류 = 신뢰['분류']
  const 최대매출 = Math.max(1, ...쓰는달.map((m) => Math.abs(mv(합계, '매출액', m))))
  const 최대현금 = Math.max(1, ...현금달.map((m) =>
    Math.max(수지['수입'][m] || 0, 수지['지출'][m] || 0)))

  const 카드 = [
    ['매출액', 억(매출), '억', 끝월 ? `1~${끝월}월 누계` : ''],
    ['영업이익', 억(영업), '억', 매출 ? `영업이익률 ${pct(률(영업, 매출))}` : ''],
    ['당기순이익', 억(순익), '억', ''],
    ['보유자금', 억(잔액['합계']), '억',
      `${잔액['기준일'].slice(5)} 현재 · 은행 ${억(잔액['은행'])} · 증권 ${억(잔액['증권'])}`],
    ['당월 순증감', 억(당월수입 - 당월지출), '억',
      `수입 ${억(당월수입)} · 지출 ${억(당월지출)}`],
  ]

  return (
    <>
      <div style={{ position: 'relative' }} className="no-print">
        <h2>경영보고</h2>
        <NoteDrawer target="exec_report" screenKey="exec_report" context={{ month }}
          contextLabel={month} autoOpenNoteId={autoOpenNoteId}
          onAutoOpened={onAutoOpened} currentUserId={currentUserId} />
      </div>

      <div className="row no-print" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <span className="muted">기준월</span>
        <input type="month" value={month} max={이번달}
          title="이번 달은 아직 끝나지 않아 수지와 계획 대비가 진행 중인 값입니다."
          onChange={(e) => setMonth(e.target.value || 이번달)} />
        <button type="button" className="btn" onClick={() => window.print()}>인쇄 · PDF</button>
        <span className="muted" style={{ fontSize: 12 }}>
          자금 {잔액['기준일']} 기준 · 손익 {d['손익']['기준']
            ? String(d['손익']['기준']['uploaded_at']).slice(0, 10) + ' 업로드분' : '자료 없음'}
        </span>
      </div>

      <div className="print-only" style={{ marginBottom: 10 }}>
        <b style={{ fontSize: 17 }}>경영보고 {month}</b>
        <span className="muted" style={{ marginLeft: 10, fontSize: 12 }}>
          자금 {잔액['기준일']} 기준
        </span>
      </div>

      <div className="ov-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {카드.map(([k, val, u, sub]) => (
          <div key={k} className={'ov' + (k === '매출액' ? ' ov-lead' : '')}>
            <div className="ov-k">{k}</div>
            <div className={'ov-v sm' + (String(val).startsWith('-') ? ' bad' : '')}>
              {val}<small>{u}</small>
            </div>
            <div className="ov-d">{sub}</div>
          </div>
        ))}
      </div>

      {/* ① 사업부 손익 */}
      <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
        <b>사업부 손익</b>
        <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
          {끝월 ? `1~${끝월}월 누계 · ` : ''}백만원 · 위하고 손익계산서
        </span>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ marginTop: 10 }}>
            <thead><tr>
              <th className="l">사업부</th><th>매출액</th><th>매출원가</th><th>매출총이익</th>
              <th>판관비</th><th>영업이익</th><th>영업이익률</th><th>당기순이익</th>
            </tr></thead>
            <tbody>
              {사업부.map((div) => {
                const s = d['손익']['요약'][div]
                const 매 = v(s, '매출액')
                const 영 = v(s, '영업이익')
                return (
                  <tr key={div} className={div === 합계이름 ? 'total' : ''}>
                    <td className="l">{div}{div === 합계이름 ? ' (회사 전체)' : ''}</td>
                    <td className="fp-cell">{백만(매)}</td>
                    <td className="fp-cell">{백만(v(s, '매출원가'))}</td>
                    <td className="fp-cell">{백만(v(s, '매출총이익'))}</td>
                    <td className="fp-cell">{백만(v(s, '판관비'))}</td>
                    <td className={'fp-cell' + (영 < 0 ? ' fp-bad' : '')}>{백만(영)}</td>
                    <td className={'fp-cell' + (영 < 0 ? ' fp-bad' : '')}>{pct(률(영, 매))}</td>
                    <td className={'fp-cell' + (v(s, '당기순이익') < 0 ? ' fp-bad' : '')}>
                      {백만(v(s, '당기순이익'))}
                    </td>
                  </tr>
                )
              })}
              {!사업부.length && <tr><td className="c muted" colSpan={8}>
                손익계산서 자료가 없습니다 — 결산 &gt; 손익계산서에서 위하고 파일을 올려주세요.
              </td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        {/* ② 손익 추이 */}
        <div className="panel" style={{ padding: 16, flex: '1 1 380px' }}>
          <b>매출 · 영업이익 추이</b>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>회사 합계 · 백만원</span>
          <div className="mchart">
            {!!쓰는달.length && (
              <div className="mcol mkeys">
                <div className="mbars" />
                <div className="mlab">&nbsp;</div>
                <div className="mval">매출액</div>
                <div className="mval sub">영업이익</div>
              </div>
            )}
            {쓰는달.map((m) => {
              const s = mv(합계, '매출액', m)
              const o = mv(합계, '영업이익', m)
              return (
                <div key={m} className="mcol">
                  <div className="mbars">
                    <span className="mbar sales" style={{ height: `${Math.abs(s) / 최대매출 * 100}%` }} />
                    <span className={'mbar ' + (o < 0 ? 'loss' : 'gross')}
                      style={{ height: `${Math.abs(o) / 최대매출 * 100}%` }} />
                  </div>
                  <div className="mlab">{m}월</div>
                  <div className="mval">{백만(s)}</div>
                  <div className={'mval sub' + (o < 0 ? ' neg' : '')}>{백만(o)}</div>
                </div>
              )
            })}
            {!쓰는달.length && <div className="muted">자료 없음</div>}
          </div>
          <div className="mlegend">
            <span><i className="sw sales" />매출액</span>
            <span><i className="sw gross" />영업이익</span>
          </div>
        </div>

        {/* ③ 자금 수지 */}
        <div className="panel" style={{ padding: 16, flex: '1 1 380px' }}>
          <b>자금 수지</b>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
            계좌끼리 옮긴 돈은 뺐습니다 · 백만원
          </span>
          <div className="mchart">
            {!!현금달.length && (
              <div className="mcol mkeys">
                <div className="mbars" />
                <div className="mlab">&nbsp;</div>
                <div className="mval">수입</div>
                <div className="mval sub">지출</div>
                <div className="mval sub">순증감</div>
                <div className="mval bal">월말잔액</div>
              </div>
            )}
            {현금달.map((m) => {
              const i = 수지['수입'][m] || 0
              const o = 수지['지출'][m] || 0
              const 잔 = 월잔액(m)
              return (
                <div key={m} className="mcol">
                  <div className="mbars">
                    <span className="mbar gross" style={{ height: `${i / 최대현금 * 100}%` }} />
                    <span className="mbar loss" style={{ height: `${o / 최대현금 * 100}%` }} />
                  </div>
                  <div className="mlab">{m}월</div>
                  <div className="mval">{백만(i)}</div>
                  <div className="mval sub neg">{음(o)}</div>
                  <div className={'mval sub' + (i - o < 0 ? ' neg' : '')}>{백만(i - o)}</div>
                  <div className={'mval bal' + (잔 != null && 잔 < 0 ? ' neg' : '')}>
                    {잔 == null ? '—' : 백만(잔)}
                  </div>
                </div>
              )
            })}
            {!현금달.length && <div className="muted">자료 없음</div>}
          </div>
          <div className="mlegend">
            <span><i className="sw gross" />수입</span>
            <span><i className="sw loss" />지출</span>
            <span className="muted">
              월말잔액은 은행 계좌 합계입니다{현금달.includes(기준월) && 기준연 === d['year']
                ? ` · ${기준월}월은 ${잔액['기준일'].slice(5)} 현재` : ''}
            </span>
          </div>
          {당월대체 > 0 && (
            <div className="hint" style={{ marginTop: 8 }}>
              {이번}월에 <b>계좌 간 이동(대체) {억(당월대체)}억</b>을 수입·지출에서 뺐습니다.
              잔액 증감이 위 순증감과 다르면 이 금액을 먼저 확인하세요.
            </div>
          )}
        </div>
      </div>

      <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        {/* ④ 자금계획 대비 */}
        <div className="panel" style={{ padding: 16, flex: '1 1 340px' }}>
          <b>{이번}월 자금계획 대비</b>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>백만원</span>
          <table style={{ marginTop: 10 }}>
            <thead><tr><th className="l">구분</th><th>계획</th><th>실적</th><th>차이</th></tr></thead>
            <tbody>
              {/* 지출은 부호를 뒤집어 담는다 - 같은 열에서 순증감만 마이너스면 읽다가 걸린다.
                  부호를 맞추면 차이 칸도 규칙이 하나가 된다: 마이너스면 계획보다 나쁘다. */}
              {[['수입', 계획수입, 당월수입, 1], ['지출', 계획지출, 당월지출, -1]]
                .map(([k, p, a, s]) => (
                <tr key={k}>
                  <td className="l">{k}</td>
                  <td className="fp-cell">{p ? 백만(s * p) : '—'}</td>
                  <td className={'fp-cell' + (s < 0 ? ' fp-bad' : '')}>{a ? 백만(s * a) : 백만(0)}</td>
                  <td className={'fp-cell' + (p && s * (a - p) < 0 ? ' fp-bad' : '')}>
                    {p ? 백만(s * (a - p)) : '—'}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td className="l">순증감</td>
                <td className="fp-cell">{계획수입 || 계획지출 ? 백만(계획수입 - 계획지출) : '—'}</td>
                <td className={'fp-cell' + (당월수입 - 당월지출 < 0 ? ' fp-bad' : '')}>
                  {백만(당월수입 - 당월지출)}
                </td>
                <td className="fp-cell">
                  {계획수입 || 계획지출
                    ? 백만((당월수입 - 당월지출) - (계획수입 - 계획지출)) : '—'}
                </td>
              </tr>
            </tbody>
          </table>
          {!(계획수입 || 계획지출) && (
            <div className="hint" style={{ marginTop: 8 }}>
              이 달 자금계획이 아직 없습니다 — 자금 &gt; 자금계획에서 입력하면 여기 같이 나옵니다.
            </div>
          )}
        </div>

        {/* ⑤ 생산 · 원가 */}
        <div className="panel" style={{ padding: 16, flex: '1 1 340px' }}>
          <b>생산 · 원가</b>
          <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
            {원가['월'] ? `최근 자료 ${원가['월']}` : '자료 없음'}
          </span>
          <table style={{ marginTop: 10 }}>
            <tbody>
              {원가['월'] ? [
                ['생산 품목', `${(원가['품목수'] || 0).toLocaleString()}건`],
                ['생산 수량', (원가['생산수량'] || 0).toLocaleString()],
                ['제조원가', `${백만(원가['제조원가'])}백만`],
                ['수량당 원가', 원가['생산수량']
                  ? `${Math.round(원가['제조원가'] / 원가['생산수량']).toLocaleString()}원` : '—'],
              ].map(([k, v]) => (
                <tr key={k}><td className="l">{k}</td><td className="fp-cell">{v}</td></tr>
              )) : (
                <tr><td className="c muted">원가 자료가 없습니다</td></tr>
              )}
            </tbody>
          </table>
          {원가['월'] && 원가['월'] < month && (
            <div className="hint" style={{ marginTop: 8 }}>
              {month} 자료는 아직 들어오지 않았습니다 — 위 숫자는 {원가['월']} 기준입니다.
            </div>
          )}
        </div>
      </div>

      {/* ⑥ 이 숫자를 어디까지 믿어도 되는가 */}
      <div className="panel" style={{ padding: 16, marginTop: 12 }}>
        <b>자료 신뢰도</b>
        <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
          숫자보다 먼저 봐야 할 칸입니다 — 원장이 어디까지 들어왔고, 분류가 얼마나 붙었는지
        </span>
        <div className="row" style={{ gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <table style={{ flex: '1 1 300px' }}>
            <thead><tr><th className="l">원장</th><th>건수</th><th>마지막 자료</th></tr></thead>
            <tbody>
              {Object.entries(신뢰['수집']).map(([k, s]) => (
                <tr key={k}>
                  <td className="l">{k}</td>
                  <td className="fp-cell">{(s['건수'] || 0).toLocaleString()}</td>
                  <td className="fp-cell">{s['최근'] ? String(s['최근']).slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table style={{ flex: '1 1 300px' }}>
            <thead><tr><th className="l">{이번}월 은행 거래 분류</th><th>건수</th><th>비율</th></tr></thead>
            <tbody>
              {[['전체', 분류['건수']], ['거래처 매칭', 분류['거래처']],
                ['계정과목 지정', 분류['계정과목']]].map(([k, n]) => (
                <tr key={k}>
                  <td className="l">{k}</td>
                  <td className="fp-cell">{(n || 0).toLocaleString()}</td>
                  <td className="fp-cell">
                    {k === '전체' || !분류['건수'] ? '' : ((n / 분류['건수']) * 100).toFixed(0) + '%'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="hint" style={{ marginTop: 12 }}>
        손익은 <b>위하고에서 받은 손익계산서를 그대로</b> 담은 값입니다 — 이 화면은 손익을
        계산하지 않습니다. 자금은 은행·증권 원장에서 모았고, 계좌끼리 옮긴 돈은 뺐습니다.
      </div>
    </>
  )
}
